<template>
  <div v-if="shouldShowRail" class="account-rail">
    <!-- Account list -->
    <div class="accounts-container">
      <div
        v-for="account in sortedAccounts"
        :key="account.email"
        class="account-item"
        :class="{ 
          active: account.email === activeEmail,
          switching: isSwitching && switchingTo === account.email
        }"
        :data-tooltip="account.companyName"
        @click="handleAccountClick(account)"
        @contextmenu="handleContextMenu($event, account)"
      >
        <div
          class="account-avatar"
          :style="{ backgroundColor: account.color }"
        >
          <span class="account-initials">{{ account.initials }}</span>
        </div>
        <div v-if="isSwitching && switchingTo === account.email" class="loading-spinner">
          <i class="el-icon-loading"></i>
        </div>
      </div>
    </div>

    <!-- Context menu -->
    <div
      v-if="contextMenu.visible"
      class="context-menu"
      :style="{ top: contextMenu.y + 'px', left: contextMenu.x + 'px' }"
    >
      <div class="context-menu-item logout" @click="handleRemoveAccount">
        <i class="el-icon-switch-button"></i>
        <span>{{ $t("global.account_switcher.log_out") }}</span>
      </div>
    </div>

    <!-- Divider -->
    <div class="rail-divider"></div>

    <!-- Add account button -->
    <div
      class="add-account-btn"
      :data-tooltip="$t('global.account_switcher.add_account')"
      @click="openAddAccountModal"
    >
      <i class="el-icon-plus"></i>
    </div>
    
    <!-- Add Account Modal -->
    <add-account-modal
      :visible="showAddAccountModal"
      :initial-email="modalInitialEmail"
      :mode="modalMode"
      @close="showAddAccountModal = false"
      @account-added="handleAccountAdded"
    />
  </div>
</template>

<script>
import AddAccountModal from './AddAccountModal.vue';

export default {
  name: 'AccountRail',
  
  components: {
    AddAccountModal,
  },
  
  data() {
    return {
      switchingTo: null,
      showAddAccountModal: false,
      modalInitialEmail: '',
      modalMode: 'add',
      contextMenu: {
        visible: false,
        x: 0,
        y: 0,
        account: null,
      },
    };
  },
  
  computed: {
    shouldShowRail() {
      return this.$store.getters['accountSwitcher/shouldShowRail'];
    },
    
    sortedAccounts() {
      return this.$store.getters['accountSwitcher/sortedAccounts'];
    },
    
    activeEmail() {
      return this.$store.state.accountSwitcher.activeEmail;
    },
    
    isSwitching() {
      return this.$store.state.accountSwitcher.isSwitching;
    },
  },
  
  mounted() {
    this._closeContextMenu = () => this.closeContextMenu();
    document.addEventListener('click', this._closeContextMenu);
  },

  beforeDestroy() {
    document.removeEventListener('click', this._closeContextMenu);
  },

  methods: {
    async handleAccountClick(account) {
      if (account.email === this.activeEmail) {
        return; // Already active
      }

      if (this.isSwitching) {
        return; // Already switching
      }

      this.switchingTo = account.email;

      try {
        const result = await this.$store.dispatch('accountSwitcher/switchAccount', account.email);
        if (result && result.requiresPin) {
          this.openSwitchVerificationModal(account.email);
          this.switchingTo = null;
        }
      } catch (error) {
        this.$message({
          type: 'error',
          message: error.message || this.$t('global.account_switcher.switch_failed'),
          duration: 3000,
        });
        this.switchingTo = null;
      }
    },

    openAddAccountModal() {
      this.modalMode = 'add';
      this.modalInitialEmail = '';
      this.showAddAccountModal = true;
    },

    openSwitchVerificationModal(email) {
      this.modalMode = 'switch';
      this.modalInitialEmail = email;
      this.showAddAccountModal = true;
    },

    handleAccountAdded() {
      // Account was added successfully - modal handles the page reload
    },

    handleContextMenu(event, account) {
      event.preventDefault();
      this.contextMenu = {
        visible: true,
        x: event.clientX,
        y: event.clientY,
        account,
      };
    },

    closeContextMenu() {
      this.contextMenu.visible = false;
      this.contextMenu.account = null;
    },

    async handleRemoveAccount() {
      const account = this.contextMenu.account;
      if (!account) return;

      this.closeContextMenu();

      try {
        await this.$confirm(
          `${this.$t('global.account_switcher.log_out_of')} "${account.companyName}"?`,
          this.$t('global.account_switcher.log_out'),
          {
            confirmButtonText: this.$t('global.account_switcher.log_out'),
            cancelButtonText: this.$t('global.Cancel'),
            type: 'warning',
          }
        );
      } catch {
        return; // User cancelled
      }

      const isActive = account.email === this.activeEmail;
      const accounts = this.$store.getters['accountSwitcher/sortedAccounts'];
      const hasOtherAccounts = accounts.length > 1;

      if (isActive && !hasOtherAccounts) {
        // Last account - full logout
        await this.$store.dispatch('accountSwitcher/removeAccount', account.email);
        await this.$store.dispatch('authen/logout', { reason: 'manual' });
        return;
      }

      if (isActive && hasOtherAccounts) {
        // Active account with others remaining - remove then switch to most recently accessed
        const otherAccounts = accounts
          .filter(a => a.email !== account.email)
          .sort((a, b) => b.lastAccess - a.lastAccess);
        const switchTo = otherAccounts[0];

        await this.$store.dispatch('accountSwitcher/removeAccount', account.email);
        const result = await this.$store.dispatch('accountSwitcher/switchAccount', switchTo.email);
        if (result && result.requiresPin) {
          this.openSwitchVerificationModal(switchTo.email);
        }
        return;
      }

      // Non-active account - just remove
      await this.$store.dispatch('accountSwitcher/removeAccount', account.email);
    },
  },
};
</script>

<style lang="scss" scoped>
.account-rail {
  width: 48px;
  min-width: 48px;
  height: 100vh;
  background: linear-gradient(180deg, #4A1E8F 0%, #3B1676 100%);
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 10px 0 14px;
  position: fixed;
  left: 0;
  top: 0;
  z-index: 100;
  box-shadow:
    inset -1px 0 0 rgba(255, 255, 255, 0.04),
    2px 0 12px rgba(0, 0, 0, 0.22);
  box-sizing: border-box;
}

.accounts-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 8px 0;
  width: 100%;

  &::-webkit-scrollbar {
    width: 0;
  }
}

%rail-tooltip {
  &::after {
    content: attr(data-tooltip);
    position: absolute;
    left: 46px;
    top: 50%;
    transform: translateY(-50%) translateX(-8px);
    background: #1A1A2E;
    color: #fff;
    padding: 6px 10px;
    border-radius: 8px;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.1px;
    white-space: nowrap;
    opacity: 0;
    visibility: hidden;
    transition: all 160ms cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow:
      0 8px 20px rgba(0, 0, 0, 0.28),
      0 1px 3px rgba(0, 0, 0, 0.18);
    border: 1px solid rgba(255, 255, 255, 0.04);
    z-index: 200;
    pointer-events: none;
  }

  &::before {
    content: '';
    position: absolute;
    left: 42px;
    top: 50%;
    transform: translateY(-50%) translateX(-8px);
    border-width: 5px;
    border-style: solid;
    border-color: transparent #1A1A2E transparent transparent;
    opacity: 0;
    visibility: hidden;
    transition: all 160ms cubic-bezier(0.4, 0, 0.2, 1);
    z-index: 200;
    pointer-events: none;
  }

  &:hover {
    &::after, &::before {
      opacity: 1;
      visibility: visible;
      transform: translateY(-50%) translateX(0);
    }
  }
}

.account-item {
  position: relative;
  width: 36px;
  height: 36px;
  cursor: pointer;
  border-radius: 10px;
  flex-shrink: 0;
  @extend %rail-tooltip;

  &:hover .account-avatar {
    border-radius: 10px;
    transform: translateY(-1px);
    box-shadow:
      0 4px 12px rgba(0, 0, 0, 0.3),
      inset 0 0 0 1px rgba(255, 255, 255, 0.08);
  }

  &.active {
    .account-avatar {
      border-radius: 10px;
      box-shadow:
        0 0 0 2px #fff,
        0 0 10px 0 rgba(255, 194, 0, 0.75),
        0 4px 12px rgba(0, 0, 0, 0.32);
      transform: scale(1.03);
    }
  }

  &.switching {
    pointer-events: none;
    opacity: 0.7;

    .account-avatar {
      filter: blur(1px);
    }
  }
}

.account-avatar {
  width: 36px;
  height: 36px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition:
    border-radius 200ms cubic-bezier(0.25, 0.8, 0.25, 1),
    transform 160ms ease,
    box-shadow 160ms ease;
  box-shadow:
    0 2px 6px rgba(0, 0, 0, 0.22),
    inset 0 0 0 1px rgba(255, 255, 255, 0.06);
  background-size: cover;
  background-position: center;
  border: 2px solid transparent;
  box-sizing: border-box;
}

.account-initials {
  color: #fff;
  font-size: 13px;
  font-weight: 800;
  letter-spacing: 0.2px;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.25);
  user-select: none;
}

.loading-spinner {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.42);
  border-radius: 10px;

  i {
    color: #fff;
    font-size: 16px;
    animation: spin 1s linear infinite;
  }
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.rail-divider {
  width: 28px;
  height: 1px;
  margin: 6px 0;
  flex-shrink: 0;
  background: linear-gradient(
    90deg,
    transparent 0%,
    rgba(255, 255, 255, 0.18) 50%,
    transparent 100%
  );
}

.add-account-btn {
  position: relative;
  width: 36px;
  height: 36px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition:
    background 160ms ease,
    border-color 160ms ease,
    border-radius 200ms cubic-bezier(0.25, 0.8, 0.25, 1),
    transform 160ms ease;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.14);
  flex-shrink: 0;
  @extend %rail-tooltip;

  i {
    color: rgba(255, 255, 255, 0.7);
    font-size: 15px;
    transition: color 160ms ease;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.12);
    border-color: rgba(255, 255, 255, 0.28);
    border-radius: 10px;

    i {
      color: #fff;
    }
  }

  &:active {
    transform: scale(0.96);
  }
}

.context-menu {
  position: fixed;
  background: #fff;
  border-radius: 10px;
  box-shadow:
    0 12px 28px rgba(30, 10, 60, 0.16),
    0 2px 6px rgba(30, 10, 60, 0.08);
  border: 1px solid rgba(0, 0, 0, 0.05);
  padding: 6px;
  z-index: 9999;
  min-width: 160px;
}

.context-menu-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  transition: background 140ms ease, color 140ms ease;

  &.logout {
    color: #CC3030;

    &:hover {
      background: rgba(204, 48, 48, 0.08);
    }
  }

  i {
    font-size: 14px;
  }
}

/* Responsive - hide on very small screens */
@media only screen and (max-width: 768px) {
  .account-rail {
    display: none;
  }
}
</style>
